import React from "react";
import Image from "@Components/Image";
import HorizontalScroll from "@Components/HorizontalScroll";
import PlayIcon from "@Icons/PlayIcon";
import useTranslationHook from "hooks/useTranslationHook";
import { PhotoItem } from "../../types";
import { getReviewMediaThumbUrl, REVIEW_MEDIA_SIZES } from "../../utils";
import styles from "./photoGallery.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";

interface PhotoGalleryProps {
  photos: PhotoItem[];
  onMediaClick?: (index: number) => void;
  compact?: boolean;
}

const MAX_THUMBNAILS = 7;

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos, onMediaClick, compact = false }) => {
  const { t } = useTranslationHook();
  const visiblePhotos = photos.slice(0, MAX_THUMBNAILS);

  if (visiblePhotos.length === 0) {
    return null;
  }

  return (
    <HorizontalScroll
      className={`${styles.container}${compact ? ` ${styles.compact}` : ""}`}
      itemSize={compact ? 112 : 152}
    >
      {visiblePhotos.map((photo, index) => {
        const isLast = index === visiblePhotos.length - 1;
        const showOverlay = isLast && (photo.overlayCount || photo.overlayText);
        const overlayLabel = photo.overlayCount
          ? t("propertypage.reviews.morephotos", { count: String(photo.overlayCount) })
          : photo.overlayText;
        const thumbSize = compact ? "overviewCompact" : "overview";
        const { width: thumbWidth, height: thumbHeight } = REVIEW_MEDIA_SIZES[thumbSize];

        return (
          <div
            key={photo.id}
            className={`${styles.thumbnailWrapper}${compact ? ` ${styles.compactThumbnail}` : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => onMediaClick?.(index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onMediaClick?.(index);
              }
            }}
            aria-label={photo.isVideo ? `Play video ${index + 1}` : `View photo ${index + 1}`}
          >
            <Image
              src={getReviewMediaThumbUrl(photo, thumbSize)}
              alt={photo.alt}
              width={thumbWidth}
              height={thumbHeight}
              className={`${styles.thumbnail}${compact ? ` ${styles.compactImg}` : ""}`}
            />
            {photo.isVideo && (
              <div className={styles.videoOverlay}>
                <div className={styles.playButton}>
                  <PlayIcon width="14" height="14" fill="white" />
                </div>
              </div>
            )}
            {showOverlay && (
              <div className={styles.textOverlay}>
                <span className={styles.overlayText}>{overlayLabel}</span>
              </div>
            )}
          </div>
        );
      })}
    </HorizontalScroll>
  );
};

export default wrapperHOC(PhotoGallery, {
  componentName: "PhotoGallery",
  showForChina: true,
});
